import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  Building2,
  Store,
  DoorOpen,
  ShoppingBag,
  Banknote,
  Wallet,
  FileBarChart,
  BarChart3,
  FileDown,
  HelpCircle,
  Phone,
  Contact,
  MessageSquare,
  Compass,
  ScrollText,
  Users,
  Megaphone,
  Ticket,
  Landmark,
  User,
  KeyRound,
  QrCode,
  Percent,
  ShieldAlert,
  UserCog,
  Settings2,
  MonitorSmartphone,
  Share2,
  Truck,
  Boxes,
  CalendarClock,
  ChefHat,
  Lock,
  type LucideIcon,
} from 'lucide-react';
import { useAuthStore } from '@/core/auth/authStore';
import type { AdminRole } from '@/core/auth/types';
import { type FeatureFlag, isEnabled } from '@/core/config/featureFlags';
import { useUiStore } from '@/core/ui/uiStore';
import { cn } from '@/core/lib/cn';

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  roles: AdminRole[];
  /** Reserved-feature gate: nav item is hidden when the flag is off. */
  flag?: FeatureFlag;
  /**
   * Locked placeholder: rendered as a disabled, non-clickable item with a lock
   * badge (mirrors the Flutter drawer/dashboard locked cards). Shown for matching
   * roles regardless of its backing feature flag, so testers see the same
   * "locked" state on web and Flutter.
   */
  locked?: boolean;
}

interface NavSection {
  /** Section heading (Flutter-drawer style). Omitted for the top Dashboard row. */
  title?: string;
  items: NavItem[];
}

const SUPER: AdminRole[] = ['SUPER_ADMIN'];
const TECH: AdminRole[] = ['TECH_ADMIN', 'SUPER_ADMIN'];
const BOTH: AdminRole[] = ['SUPER_ADMIN', 'TECH_ADMIN'];

// Grouped to mirror the Flutter Admin drawer sections. Labels use the Flutter
// equivalent where the workflow matches (Live Orders, Finance Analytics,
// Financial Reports, Report Access Config, Manage FAQs, Contact Details).
const SECTIONS: NavSection[] = [
  { items: [{ to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, roles: BOTH }] },
  {
    title: 'Tenants',
    items: [
      { to: '/tenants', label: 'Tenants', icon: Building2, roles: SUPER },
      { to: '/employees', label: 'Employees', icon: Users, roles: SUPER },
    ],
  },
  {
    title: 'Vendors',
    items: [
      { to: '/vendors', label: 'Vendors', icon: Store, roles: SUPER },
      { to: '/vendors/lobby', label: 'Lobby', icon: DoorOpen, roles: SUPER },
      { to: '/vendors/payouts', label: 'Vendor Payouts', icon: Banknote, roles: SUPER },
    ],
  },
  {
    title: 'Orders',
    items: [
      { to: '/orders', label: 'Orders', icon: ShoppingBag, roles: SUPER },
      { to: '/pickups', label: 'Pickups', icon: Truck, roles: SUPER, locked: true },
      { to: '/bulk-orders', label: 'Bulk Orders', icon: Boxes, roles: SUPER, locked: true },
      { to: '/pre-orders', label: 'Pre-orders', icon: CalendarClock, roles: SUPER, locked: true },
      { to: '/kds-batches', label: 'KDS Batches', icon: ChefHat, roles: SUPER, locked: true },
    ],
  },
  {
    title: 'Finance / Reports',
    items: [
      { to: '/finance', label: 'Finance Analytics', icon: Wallet, roles: SUPER },
      { to: '/finance/financial-report', label: 'Financial Reports', icon: FileBarChart, roles: SUPER },
      { to: '/reports/access', label: 'Report Access Config', icon: BarChart3, roles: TECH },
      { to: '/reports', label: 'Data Exports', icon: FileDown, roles: TECH, flag: 'dataExports' },
    ],
  },
  {
    title: 'Support',
    items: [
      { to: '/support/faqs', label: 'Manage FAQs', icon: HelpCircle, roles: SUPER },
      // Flutter "Contact Details" (PLATFORM drawer) is the platform-wide contact;
      // FAQ category contacts are a separate surface.
      { to: '/support/contacts', label: 'FAQ Contacts', icon: Phone, roles: SUPER },
      { to: '/support/platform-contact', label: 'Contact Details', icon: Contact, roles: SUPER },
      { to: '/support/announcements', label: 'Announcements', icon: MessageSquare, roles: SUPER },
    ],
  },
  {
    title: 'Discovery',
    items: [
      { to: '/discovery', label: 'Discovery', icon: Compass, roles: SUPER },
      { to: '/coupons', label: 'Coupons', icon: Ticket, roles: SUPER },
    ],
  },
  {
    title: 'Notifications',
    items: [
      { to: '/broadcasts', label: 'Broadcasts', icon: Megaphone, roles: SUPER },
      { to: '/notif-analytics', label: 'Notif Analytics', icon: BarChart3, roles: SUPER },
    ],
  },
  {
    title: 'Platform',
    items: [
      { to: '/manage-admins', label: 'Manage Tech Admins', icon: UserCog, roles: SUPER, flag: 'manageAdmins' },
      { to: '/platform-settings', label: 'Platform Settings', icon: Settings2, roles: SUPER },
      { to: '/platform/vendor-fees', label: 'Vendor Commission', icon: Percent, roles: SUPER },
      { to: '/finance-profile', label: 'Finance Profile', icon: Landmark, roles: TECH },
      { to: '/api-keys', label: 'API Keys', icon: KeyRound, roles: TECH, locked: true },
      { to: '/qr-config', label: 'QR Config', icon: QrCode, roles: TECH, locked: true },
      { to: '/security-incidents', label: 'Security', icon: ShieldAlert, roles: TECH, locked: true },
      { to: '/sessions', label: 'Sessions', icon: MonitorSmartphone, roles: BOTH, locked: true },
      { to: '/delegation', label: 'Delegation', icon: Share2, roles: SUPER, locked: true },
    ],
  },
  {
    title: 'Audit',
    items: [{ to: '/audit', label: 'Audit Logs', icon: ScrollText, roles: SUPER }],
  },
  {
    title: 'Profile',
    items: [{ to: '/profile', label: 'Profile', icon: User, roles: BOTH }],
  },
];

export function Sidebar() {
  const role = useAuthStore((s) => s.role);
  const collapsed = useUiStore((s) => s.sidebarCollapsed);
  const setSidebarCollapsed = useUiStore((s) => s.setSidebarCollapsed);

  const handleNavClick = () => {
    if (window.innerWidth < 768) {
      setSidebarCollapsed(true);
    }
  };

  // An item is visible if the role matches and either it is a locked placeholder
  // (always shown for the role) or its feature flag is enabled.
  const visible = (n: NavItem): boolean =>
    (role ? n.roles.includes(role) : false) && (n.locked === true || !n.flag || isEnabled(n.flag));

  const sections = SECTIONS.map((s) => ({ ...s, items: s.items.filter(visible) })).filter(
    (s) => s.items.length > 0,
  );

  // A parent item (e.g. /finance) must use exact matching so it isn't highlighted
  // when a sibling child route (e.g. /finance/financial-report) is active.
  const allTos = sections.flatMap((s) => s.items.map((i) => i.to));
  const exactMatch = (to: string): boolean => allTos.some((other) => other.startsWith(`${to}/`));

  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-50 flex shrink-0 flex-col bg-fv-ink text-white transition-all duration-200 ease-in-out md:relative md:translate-x-0',
        collapsed ? '-translate-x-full md:w-16 md:translate-x-0' : 'translate-x-0 md:w-60',
      )}
    >
      <div
        className={cn(
          'flex items-center py-5 font-bold',
          collapsed ? 'justify-center px-2 text-base' : 'px-5 text-lg',
        )}
        title={collapsed ? 'Food Valve Admin' : undefined}
      >
        {collapsed ? (
          <span className="text-fv-primary">FV</span>
        ) : (
          <>
            Food Valve <span className="text-fv-primary">Admin</span>
          </>
        )}
      </div>
      <nav className={cn('flex-1 space-y-1 overflow-y-auto pb-4', collapsed ? 'px-2' : 'px-3')}>
        {sections.map((section, i) => (
          <div key={section.title ?? `section-${i}`} className="space-y-1">
            {section.title &&
              (collapsed ? (
                // Collapsed rail has no room for headings — separate groups with a divider.
                i > 0 && <div className="mx-2 my-2 border-t border-white/10" />
              ) : (
                <p className="px-3 pb-1 pt-3 text-[11px] font-bold uppercase tracking-wide text-white/35">
                  {section.title}
                </p>
              ))}
            {section.items.map((item) =>
              item.locked ? (
                <div
                  key={item.to}
                  aria-disabled
                  title={collapsed ? `${item.label} (Locked)` : 'Locked'}
                  className={cn(
                    'flex cursor-not-allowed items-center gap-3 rounded-lg py-2 text-sm text-white/30',
                    collapsed ? 'justify-center px-0' : 'px-3',
                  )}
                >
                  <item.icon size={18} />
                  {!collapsed && (
                    <>
                      <span className="flex-1">{item.label}</span>
                      <Lock size={13} />
                    </>
                  )}
                </div>
              ) : (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={exactMatch(item.to)}
                  title={collapsed ? item.label : undefined}
                  onClick={handleNavClick}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-lg py-2 text-sm transition-colors',
                      collapsed ? 'justify-center px-0' : 'px-3',
                      isActive
                        ? 'bg-fv-primary text-white'
                        : 'text-white/70 hover:bg-white/10 hover:text-white',
                    )
                  }
                >
                  <item.icon size={18} />
                  {!collapsed && item.label}
                </NavLink>
              ),
            )}
          </div>
        ))}
      </nav>
    </aside>
  );
}
